import React, { useState } from 'react'
import { Field } from 'redux-form'
import { useSelector } from 'react-redux'
import { toast } from 'react-toastify'

import WrappedInput from 'components/Shared/Forms/WrappedInput'
import Button from 'components/Shared/Button'
import { isCurrentUserAdaloAdmin } from 'ducks/users'
import { adminUnlinkGoogleForUser, adminUpdatePassword } from 'utils/io'

/** Must match reduxForm({ form }) on admin user pages that include ChangePasswordForm. */
export const ADMIN_USER_SECURITY_FORM_NAME = 'adminResetPasswordForm'

export const accountHasPassword = user => user && user.hasPassword !== false

export const ChangePasswordForm = ({ userId, user, onUserUpdated }) => {
  const isAdaloAdmin = useSelector(isCurrentUserAdaloAdmin)
  const [password, setPassword] = useState('')
  const addingPassword = user && !accountHasPassword(user)

  if (!isAdaloAdmin) {
    return null
  }

  const verifyPasswordChange = async e => {
    e.preventDefault()

    const confirmMessage = addingPassword
      ? 'Set a password for this user? They can use it to sign in with email and password.'
      : 'Are you sure you want to change this user’s password?'

    if (window.confirm(confirmMessage)) {
      try {
        await adminUpdatePassword({ password, userId })

        setPassword('')
        toast.success(
          addingPassword
            ? 'Password set successfully.'
            : 'Password updated successfully.'
        )
        onUserUpdated?.()
      } catch (err) {
        console.error(`Could not update password: ${err.message}`)
        toast.error(`Failed to update password: ${err.message}`)
      }
    }
  }

  return (
    <form onSubmit={verifyPasswordChange} className="admin-reset-password-form">
      <input
        type="text"
        name="email"
        autoComplete="username email"
        style={{ display: 'none' }}
      />
      <div className="admin-password-field-wrap">
        <Field
          autoFocus
          name="password"
          placeholder="New password"
          autoComplete="new-password"
          type="password"
          label={addingPassword ? 'Password' : 'New password'}
          className="admin-password-field"
          maxLength={200}
          onChange={e => setPassword(e.target.value)}
          component={WrappedInput}
          value={password}
        />
      </div>
      <div className="admin-password-submit">
        <Button large yellow type="submit">
          {addingPassword ? 'Add password' : 'Change password'}
        </Button>
      </div>
    </form>
  )
}

export const AdminDisconnectGoogle = ({ user, userId, onUserUpdated }) => {
  const isAdaloAdmin = useSelector(isCurrentUserAdaloAdmin)
  const [busy, setBusy] = useState(false)

  if (!isAdaloAdmin) {
    return null
  }

  const googleLinked =
    user.googleId != null && String(user.googleId).trim() !== ''
  const hasPwd = accountHasPassword(user)
  const canDisconnect = googleLinked && hasPwd

  const handleDisconnect = async () => {
    if (!canDisconnect || busy) {
      return
    }

    if (
      !window.confirm(
        'Disconnect Google from this user? They will need email/password (or link Google again) to sign in.'
      )
    ) {
      return
    }

    setBusy(true)
    try {
      await adminUnlinkGoogleForUser({ userId })
      toast.success('Google disconnected for this user.')
      onUserUpdated?.()
    } catch (err) {
      toast.error(err.message || 'Failed to disconnect Google.')
    } finally {
      setBusy(false)
    }
  }

  const disabledTitle = !googleLinked
    ? undefined
    : !hasPwd
    ? 'Set a password for this user before disconnecting Google (so they can still sign in).'
    : undefined

  return (
    <div className="admin-google-unlink-row">
      <p className="admin-google-unlink-label">Google</p>
      {!googleLinked ? (
        <span className="admin-muted">Not connected</span>
      ) : (
        <div className="admin-google-unlink-actions">
          <Button
            type="button"
            large
            outlined
            teal
            disabled={!canDisconnect || busy}
            title={disabledTitle}
            onClick={handleDisconnect}
          >
            {busy ? 'Working…' : 'Disconnect Google'}
          </Button>
          {googleLinked && !hasPwd ? (
            <p className="admin-google-unlink-hint admin-muted">
              Set a password for this user first (button above), so they can
              still sign in after Google is disconnected.
            </p>
          ) : null}
        </div>
      )}
    </div>
  )
}
